import {DateTime} from "luxon";

/**
 * One soft-deleted row as the restorable endpoint returns it (O2D-PRD-101).
 *
 * This is a TRANSPORT shape, not an entity: the Papierkorb needs a label, a type key and two
 * audit fields, nothing else. Hydrating a real `Entity` per row is exactly what the old dialog
 * did — it forced a frontend `Entity` class plus a transformer plus a hand-kept mirror of the
 * backend whitelist for every restorable type, and that mirror is what silently dropped
 * `linkview` (Inkrement 0041): the backend knew the type, the frontend did not, and the restore
 * path was dead for the user with nothing to see. A flat row has no such list to drift.
 *
 * `deletedAt` arrives as a string in the house wire format `Y-m-d H:i:s.u P`
 * (`RestorableEntitiesAction.php:158`), i.e. `2026-09-20 11:22:33.000000 +02:00` — space
 * separator, microseconds, trailing UTC offset. `DateTime.fromSQL` understands all three
 * unaided, which is why every `dto-to-*.transformer.ts` parses the same format with it and
 * nothing else. There is deliberately NO ISO branch any more: the trailing offset used to trip
 * an "is this ISO?" guess, `fromISO` then choked on the space, and every row silently lost its
 * date (review finding, 2026-09-20).
 */
export interface RestorableRowDto {
    readonly entityType: string;
    readonly uid: string;
    readonly label: string;
    readonly deletedAt: string | null;
    readonly deletedBy: string | null;
}

/** The same row after parsing — what the dialog renders and what the restore command carries. */
export interface RestorableRow {
    /** Whitelist key, snake_case, e.g. `knowledge_object` (RestoreTypeMap.php). */
    readonly entityType: string;
    /** Transport spelling of the uid (ShortId). */
    readonly uid: string;
    /** `__toString()` of the deleted entity, as the server rendered it. */
    readonly label: string;
    readonly deletedAt: DateTime | null;
    /** Display name of the deleting person, or `null` when the audit log has no row for it. */
    readonly deletedBy: string | null;
}

/**
 * The row identity.
 *
 * Deliberately `entityType:uid` and not the bare uid: uids are only unique WITHIN a type
 * (`ShortId` is minted per entity table), so two types could collide on one key — and
 * `<o2d-table>` reconciles by `rowKey`, which would then render one row for two entries.
 * The same string keys the in-flight set in the dialog, for the same reason.
 */
export function restorableRowKey(row: Pick<RestorableRow, 'entityType' | 'uid'>): string {
    return `${row.entityType}:${row.uid}`;
}

/** Parses one wire row. Unparseable or absent `deletedAt` becomes `null`, never an invalid DateTime. */
export function toRestorableRow(dto: RestorableRowDto): RestorableRow {
    return {
        entityType: dto.entityType,
        uid: dto.uid,
        label: dto.label ?? '',
        deletedAt: parseDeletedAt(dto.deletedAt),
        deletedBy: normalizeDeletedBy(dto.deletedBy),
    };
}

/**
 * What the date column SORTS by — epoch milliseconds, never the rendered text.
 *
 * `<o2d-table>` falls back to `filterValue` when a column defines no `sortValue`
 * (`controllers/column-accessor.ts:11`), and `filterValue` here is the LOCALIZED date string —
 * sorting that would order "1. Feb." before "1. Jan." because it compares text. A row without a
 * date sorts to the very bottom, not to 1970.
 */
export function deletedAtSortValue(row: RestorableRow): number {
    return row.deletedAt?.toMillis() ?? Number.NEGATIVE_INFINITY;
}

/** The rendered date, or `unknownLabel` when the row carries none. */
export function deletedAtLabel(row: RestorableRow, unknownLabel: string): string {
    return row.deletedAt?.toLocal().toLocaleString(DateTime.DATETIME_MED) ?? unknownLabel;
}

/**
 * The rendered person, or `unknownLabel`.
 *
 * `null` is the NORMAL case, not an error: the name is reconstructed from the audit log's
 * command row, and measured coverage over the existing stock is 16 of 540 — everything deleted
 * before the command log existed, everything a migration or the demo seed removed, and the
 * `guest` sentinel all arrive empty. The label has to read like a plain fact, not like a defect.
 *
 * The caller passes the translated text rather than this module reaching for `lit-translate`:
 * this file is also on the command path (`restore-entity.command.ts` carries a `RestorableRow`),
 * and it has no business pulling the i18n layer in there.
 */
export function deletedByLabel(row: RestorableRow, unknownLabel: string): string {
    const name = row.deletedBy?.trim() ?? '';
    return name === '' ? unknownLabel : name;
}

function parseDeletedAt(raw: string | null | undefined): DateTime | null {
    if (typeof raw !== 'string' || raw.trim() === '') {
        return null;
    }

    // The house format carries its own offset, so no zone assumption belongs here — the same
    // call, with the same absence of options, that `dto-to-issue.transformer.ts:79` makes.
    const parsed = DateTime.fromSQL(raw.trim());

    return parsed.isValid ? parsed : null;
}

function normalizeDeletedBy(raw: string | null | undefined): string | null {
    const name = typeof raw === 'string' ? raw.trim() : '';
    return name === '' ? null : name;
}
